/**
 * ExplainDrawer — Right-side drawer for graph edge details and evidence
 *
 * Shows:
 *  - Graph edge details (edge_id, confidence, derivation, authority_level)
 *  - Evidence refs list
 *  - "Explain in graph" button that calls /truth/graph/explain
 *  - For attribution slices: all active edge_ids + evidence union
 */
import {
  X, ExternalLink, Shield, GitBranch, Hash, Clock, FileText,
  ChevronDown, ChevronRight, Loader2, Package, User, Zap, ArrowRight, MapPin,
  AlertTriangle,
} from 'lucide-react';
import { useState } from 'react';
import type {
  TimelineSelection,
  GraphExplainResponse,
  GraphEdgeOverlay,
  OrderOverlay,
  PlannedOrderOverlay,
  DriverOverlay,
  FuelOverlay,
  AttributionSlice,
  MovementSegment,
} from '../../types/vehicleSceneTimeline';
import {
  formatConfidence,
  formatDateTime,
  AUTHORITY_COLORS,
} from '../../utils/timelineUtils';

interface ExplainDrawerProps {
  selection: TimelineSelection | null;
  explainData: GraphExplainResponse | null;
  explainLoading: boolean;
  explainError?: string | null;
  onExplainEdge: (edgeId: string) => void;
  onClose: () => void;
}

// ── Rich type-specific detail panels ─────────────────────────────────────

function GraphInternals({
  edge,
  onExplain,
  explainLoading,
}: {
  edge: GraphEdgeOverlay;
  onExplain: (id: string) => void;
  explainLoading: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="vst-graph-internals-section">
      <button className="vst-evidence-toggle" onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Graph edge internals
      </button>
      {expanded && (
        <div className="vst-graph-internals-body">
          <div className="vst-edge-fields">
            <div className="vst-edge-field">
              <Hash size={12} />
              <span className="vst-edge-field-label">Edge ID</span>
              <code className="vst-edge-field-value">{edge.edge_id}</code>
            </div>
            <div className="vst-edge-field">
              <GitBranch size={12} />
              <span className="vst-edge-field-label">Derivation</span>
              <span className="vst-edge-field-value">{edge.derivation}</span>
            </div>
            <div className="vst-edge-field">
              <span className="vst-edge-field-label">From entity</span>
              <code className="vst-edge-field-value">{edge.from_entity_id}</code>
            </div>
            <div className="vst-edge-field">
              <span className="vst-edge-field-label">To entity</span>
              <code className="vst-edge-field-value">{edge.to_entity_id}</code>
            </div>
          </div>
          {edge.evidence_refs.length > 0 && (
            <>
              <span className="vst-evidence-label" style={{ marginTop: 6, display: 'block' }}>
                Evidence ({edge.evidence_refs.length})
              </span>
              <ul className="vst-evidence-list">
                {edge.evidence_refs.map(ref => (
                  <li key={ref} className="vst-evidence-item">
                    <FileText size={11} />
                    <code>{ref}</code>
                  </li>
                ))}
              </ul>
            </>
          )}
          <button
            className="vst-explain-btn"
            onClick={() => onExplain(edge.edge_id)}
            disabled={explainLoading}
          >
            {explainLoading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <ExternalLink size={14} />
            )}
            Explain in graph
          </button>
        </div>
      )}
    </div>
  );
}

function OrderDetail({
  edge,
  onExplain,
  explainLoading,
}: {
  edge: GraphEdgeOverlay;
  onExplain: (id: string) => void;
  explainLoading: boolean;
}) {
  const order = edge as OrderOverlay | PlannedOrderOverlay;
  const isPlanned = order.relationship_type === 'ORDER_PLANNED_ON_VEHICLE';
  return (
    <div className="vst-edge-card">
      <div className="vst-edge-header">
        <span
          className="vst-authority-badge"
          style={{ background: AUTHORITY_COLORS[order.authority_level] }}
        >
          {order.authority_level}
        </span>
        <span className="vst-edge-type">
          {isPlanned ? 'Planned Order' : 'Order on Vehicle'}
        </span>
      </div>

      {(order.source_order_key || order.label) && (
        <div className="vst-item-primary-label">
          <Package size={14} />
          {order.source_order_key || order.label}
        </div>
      )}

      {(order.origin_postcode || order.destination_postcode) && (
        <div className="vst-route-display">
          <MapPin size={11} className="vst-route-pin" />
          <span className="vst-route-loc">{order.origin_postcode || '—'}</span>
          <ArrowRight size={11} className="vst-route-arrow" />
          <MapPin size={11} className="vst-route-pin" />
          <span className="vst-route-loc">{order.destination_postcode || '—'}</span>
        </div>
      )}

      <div className="vst-edge-fields" style={{ marginTop: 8 }}>
        <div className="vst-edge-field">
          <Clock size={12} />
          <span className="vst-edge-field-label">Window</span>
          <span className="vst-edge-field-value">
            {formatDateTime(order.from_ts)} – {formatDateTime(order.to_ts)}
          </span>
        </div>
        <div className="vst-edge-field">
          <Shield size={12} />
          <span className="vst-edge-field-label">Confidence</span>
          <span className="vst-edge-field-value">{formatConfidence(order.confidence)}</span>
        </div>
      </div>

      {/* ── Lifecycle enrichment (from Vehicle Investigation) ────────── */}
      {(order.exit_state || order.delivery_evidence || order.entry_state) && (
        <div className="vst-lifecycle-section" style={{ marginTop: 8, padding: '8px 0', borderTop: '1px solid #374151' }}>
          <div style={{ fontSize: '0.6875rem', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>
            Order Lifecycle
          </div>
          <div className="vst-edge-fields">
            {order.entry_state && (
              <div className="vst-edge-field">
                <ArrowRight size={12} />
                <span className="vst-edge-field-label">Entry</span>
                <span className="vst-edge-field-value" style={{ textTransform: 'capitalize' }}>
                  {order.entry_state.replace(/_/g, ' ')}
                </span>
              </div>
            )}
            {order.exit_state && (
              <div className="vst-edge-field">
                <Package size={12} />
                <span className="vst-edge-field-label">Exit</span>
                <span
                  className="vst-edge-field-value"
                  style={{
                    color: order.exit_state === 'delivered' ? '#10b981'
                      : order.exit_state === 'on_vehicle' ? '#9ca3af'
                      : '#f59e0b',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}
                >
                  {order.exit_state.replace(/_/g, ' ')}
                </span>
              </div>
            )}
            {order.delivery_evidence && (
              <div className="vst-edge-field">
                <Shield size={12} />
                <span className="vst-edge-field-label">Delivery evidence</span>
                <span
                  className="vst-edge-field-value"
                  style={{
                    color: order.delivery_evidence === 'gps_confirmed' ? '#10b981'
                      : order.delivery_evidence === 'graph_confirmed' ? '#3b82f6'
                      : order.delivery_evidence === 'weak' ? '#f59e0b'
                      : '#6b7280',
                    fontWeight: 500,
                  }}
                >
                  {order.delivery_evidence.replace(/_/g, ' ')}
                </span>
              </div>
            )}
            {order.pickup_evidence && (
              <div className="vst-edge-field">
                <Shield size={12} />
                <span className="vst-edge-field-label">Pickup evidence</span>
                <span
                  className="vst-edge-field-value"
                  style={{
                    color: order.pickup_evidence === 'gps_confirmed' ? '#10b981'
                      : order.pickup_evidence === 'graph_confirmed' ? '#3b82f6'
                      : order.pickup_evidence === 'weak' ? '#f59e0b'
                      : '#6b7280',
                    fontWeight: 500,
                  }}
                >
                  {order.pickup_evidence.replace(/_/g, ' ')}
                </span>
              </div>
            )}
            {order.delivery_confidence_score != null && (
              <div className="vst-edge-field">
                <Shield size={12} />
                <span className="vst-edge-field-label">Delivery confidence</span>
                <span className="vst-edge-field-value">{formatConfidence(order.delivery_confidence_score)}</span>
              </div>
            )}
            {order.actual_load_hub_name && (
              <div className="vst-edge-field">
                <MapPin size={12} />
                <span className="vst-edge-field-label">Loaded at</span>
                <span className="vst-edge-field-value">{order.actual_load_hub_name}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <GraphInternals edge={edge} onExplain={onExplain} explainLoading={explainLoading} />
    </div>
  );
}

function DriverDetail({
  edge,
  onExplain,
  explainLoading,
}: {
  edge: GraphEdgeOverlay;
  onExplain: (id: string) => void;
  explainLoading: boolean;
}) {
  const driver = edge as DriverOverlay;
  return (
    <div className="vst-edge-card">
      <div className="vst-edge-header">
        <span
          className="vst-authority-badge"
          style={{ background: AUTHORITY_COLORS[driver.authority_level] }}
        >
          {driver.authority_level}
        </span>
        <span className="vst-edge-type">Driver on Vehicle</span>
      </div>

      {(driver.source_driver_key || driver.label) && (
        <div className="vst-item-primary-label">
          <User size={14} />
          {driver.source_driver_key || driver.label}
        </div>
      )}

      <div className="vst-edge-fields" style={{ marginTop: 8 }}>
        <div className="vst-edge-field">
          <Clock size={12} />
          <span className="vst-edge-field-label">Window</span>
          <span className="vst-edge-field-value">
            {formatDateTime(driver.from_ts)} – {formatDateTime(driver.to_ts)}
          </span>
        </div>
        <div className="vst-edge-field">
          <Shield size={12} />
          <span className="vst-edge-field-label">Confidence</span>
          <span className="vst-edge-field-value">{formatConfidence(driver.confidence)}</span>
        </div>
      </div>

      <GraphInternals edge={edge} onExplain={onExplain} explainLoading={explainLoading} />
    </div>
  );
}

function FuelDetail({
  edge,
  onExplain,
  explainLoading,
}: {
  edge: GraphEdgeOverlay;
  onExplain: (id: string) => void;
  explainLoading: boolean;
}) {
  const fuel = edge as FuelOverlay;
  return (
    <div className="vst-edge-card">
      <div className="vst-edge-header">
        <span
          className="vst-authority-badge"
          style={{ background: AUTHORITY_COLORS[fuel.authority_level] }}
        >
          {fuel.authority_level}
        </span>
        <span className="vst-edge-type">Fuel Event</span>
      </div>

      <div className="vst-item-primary-label">
        <Zap size={14} />
        {fuel.fuel_event_type || fuel.label || 'Fuel Event'}
      </div>

      {fuel.fuel_litres != null && (
        <div className="vst-fuel-amount">{fuel.fuel_litres.toFixed(1)} L</div>
      )}

      <div className="vst-edge-fields" style={{ marginTop: 8 }}>
        <div className="vst-edge-field">
          <Clock size={12} />
          <span className="vst-edge-field-label">Window</span>
          <span className="vst-edge-field-value">
            {formatDateTime(fuel.from_ts)} – {formatDateTime(fuel.to_ts)}
          </span>
        </div>
        <div className="vst-edge-field">
          <Shield size={12} />
          <span className="vst-edge-field-label">Confidence</span>
          <span className="vst-edge-field-value">{formatConfidence(fuel.confidence)}</span>
        </div>
      </div>

      <GraphInternals edge={edge} onExplain={onExplain} explainLoading={explainLoading} />
    </div>
  );
}

function SegmentDetail({ segment }: { segment: MovementSegment }) {
  return (
    <div className="vst-edge-card">
      <div className="vst-edge-header">
        <span className="vst-edge-type">Movement Segment</span>
      </div>
      <div className="vst-edge-fields">
        <div className="vst-edge-field">
          <Hash size={12} />
          <span className="vst-edge-field-label">Segment ID</span>
          <code className="vst-edge-field-value">{segment.segment_id}</code>
        </div>
        <div className="vst-edge-field">
          <span className="vst-edge-field-label">Type</span>
          <span className="vst-edge-field-value">{segment.type}</span>
        </div>
        <div className="vst-edge-field">
          <span className="vst-edge-field-label">Source</span>
          <span className="vst-edge-field-value">{segment.source}</span>
        </div>
        <div className="vst-edge-field">
          <Clock size={12} />
          <span className="vst-edge-field-label">Window</span>
          <span className="vst-edge-field-value">
            {formatDateTime(segment.start_ts)} – {formatDateTime(segment.end_ts)}
          </span>
        </div>
        {segment.confidence != null && (
          <div className="vst-edge-field">
            <Shield size={12} />
            <span className="vst-edge-field-label">Confidence</span>
            <span className="vst-edge-field-value">{formatConfidence(segment.confidence)}</span>
          </div>
        )}
        {segment.notes && (
          <div className="vst-edge-field">
            <span className="vst-edge-field-label">Notes</span>
            <span className="vst-edge-field-value">{segment.notes}</span>
          </div>
        )}
      </div>
      <div className="vst-evidence-section">
        <span className="vst-evidence-label">Evidence refs</span>
        <ul className="vst-evidence-list">
          {segment.evidence_refs.map(ref => (
            <li key={ref} className="vst-evidence-item">
              <FileText size={11} />
              <code>{ref}</code>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SliceDetail({
  slice,
  onExplain,
  explainLoading,
}: {
  slice: AttributionSlice;
  onExplain: (id: string) => void;
  explainLoading: boolean;
}) {
  return (
    <div className="vst-edge-card">
      <div className="vst-edge-header">
        <span className="vst-edge-type">Attribution Slice</span>
      </div>
      <div className="vst-edge-fields">
        <div className="vst-edge-field">
          <Hash size={12} />
          <span className="vst-edge-field-label">Slice ID</span>
          <code className="vst-edge-field-value">{slice.slice_id}</code>
        </div>
        <div className="vst-edge-field">
          <span className="vst-edge-field-label">Movement</span>
          <span className="vst-edge-field-value">{slice.movement_state}</span>
        </div>
        <div className="vst-edge-field">
          <Shield size={12} />
          <span className="vst-edge-field-label">Confidence</span>
          <span className="vst-edge-field-value">{formatConfidence(slice.confidence)}</span>
        </div>
        <div className="vst-edge-field">
          <Clock size={12} />
          <span className="vst-edge-field-label">Window</span>
          <span className="vst-edge-field-value">
            {formatDateTime(slice.start_ts)} – {formatDateTime(slice.end_ts)}
          </span>
        </div>
        <div className="vst-edge-field">
          <span className="vst-edge-field-label">Orders</span>
          <span className="vst-edge-field-value">
            {slice.active_order_ids.length > 0
              ? slice.active_order_ids.join(', ')
              : 'none'}
          </span>
        </div>
        <div className="vst-edge-field">
          <span className="vst-edge-field-label">Driver</span>
          <span className="vst-edge-field-value">
            {slice.active_driver_id || 'none'}
          </span>
        </div>
        <div className="vst-edge-field">
          <span className="vst-edge-field-label">Evidence</span>
          <span className="vst-edge-field-value">{slice.evidence_count} events</span>
        </div>
      </div>

      {/* Edge links */}
      <div className="vst-slice-edges">
        <span className="vst-evidence-label">Graph Edges</span>
        {slice.edge_ids.map(edgeId => (
          <button
            key={edgeId}
            className="vst-explain-btn vst-explain-btn-sm"
            onClick={() => onExplain(edgeId)}
            disabled={explainLoading}
          >
            {explainLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <ExternalLink size={12} />
            )}
            Explain {edgeId}
          </button>
        ))}
      </div>
    </div>
  );
}

function ExplainResult({ data }: { data: GraphExplainResponse }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="vst-explain-result">
      <button
        className="vst-evidence-toggle"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Graph Explanation — {data.edge_id}
      </button>
      {expanded && (
        <div className="vst-explain-body">
          {/* Derivation chain */}
          <div className="vst-explain-section">
            <span className="vst-evidence-label">Derivation Chain</span>
            <ol className="vst-derivation-chain">
              {data.lineage.derivation_chain.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>

          {/* Evidence details */}
          <div className="vst-explain-section">
            <span className="vst-evidence-label">
              Evidence ({data.evidence.length})
            </span>
            {data.evidence.map(ev => (
              <div key={ev.evidence_ref} className="vst-evidence-detail">
                <div className="vst-evidence-detail-header">
                  <code>{ev.evidence_ref}</code>
                  <span className="vst-evidence-source">{ev.source}</span>
                </div>
                <div className="vst-evidence-detail-body">
                  <span>Type: {ev.event_type}</span>
                  <span>Time: {formatDateTime(ev.timestamp)}</span>
                  {ev.notes && <span>{ev.notes}</span>}
                  {ev.raw_payload && (
                    <pre className="vst-evidence-payload">
                      {JSON.stringify(ev.raw_payload, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Upstream edges */}
          {data.lineage.upstream_edges.length > 0 && (
            <div className="vst-explain-section">
              <span className="vst-evidence-label">Upstream Edges</span>
              {data.lineage.upstream_edges.map(edge => (
                <div key={edge.edge_id} className="vst-upstream-edge">
                  <code>{edge.edge_id}</code> — {edge.relationship_type}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ExplainDrawer({
  selection,
  explainData,
  explainLoading,
  explainError,
  onExplainEdge,
  onClose,
}: ExplainDrawerProps) {
  if (!selection) return null;

  return (
    <div className="vst-drawer">
      <div className="vst-drawer-header">
        <h3 className="vst-drawer-title">
          {selection.type === 'segment' && 'Segment Details'}
          {selection.type === 'order' && 'Order Details'}
          {selection.type === 'driver' && 'Driver Details'}
          {selection.type === 'fuel' && 'Fuel Event Details'}
          {selection.type === 'slice' && 'Attribution Slice'}
        </h3>
        <button className="vst-drawer-close" onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      <div className="vst-drawer-body">
        {/* Render based on selection type */}
        {selection.edge && selection.type === 'order' && (
          <OrderDetail
            edge={selection.edge}
            onExplain={onExplainEdge}
            explainLoading={explainLoading}
          />
        )}
        {selection.edge && selection.type === 'driver' && (
          <DriverDetail
            edge={selection.edge}
            onExplain={onExplainEdge}
            explainLoading={explainLoading}
          />
        )}
        {selection.edge && selection.type === 'fuel' && (
          <FuelDetail
            edge={selection.edge}
            onExplain={onExplainEdge}
            explainLoading={explainLoading}
          />
        )}

        {selection.segment && (
          <SegmentDetail segment={selection.segment} />
        )}

        {selection.slice && (
          <SliceDetail
            slice={selection.slice}
            onExplain={onExplainEdge}
            explainLoading={explainLoading}
          />
        )}

        {/* Related IDs */}
        {(selection.relatedOrderIds?.length || selection.relatedDriverIds?.length || selection.relatedSegmentIds?.length) && (
          <div className="vst-related-section">
            <span className="vst-evidence-label">Related Items</span>
            {selection.relatedSegmentIds && selection.relatedSegmentIds.length > 0 && (
              <div className="vst-related-group">
                <span>Segments: {selection.relatedSegmentIds.join(', ')}</span>
              </div>
            )}
            {selection.relatedOrderIds && selection.relatedOrderIds.length > 0 && (
              <div className="vst-related-group">
                <span>Orders: {selection.relatedOrderIds.join(', ')}</span>
              </div>
            )}
            {selection.relatedDriverIds && selection.relatedDriverIds.length > 0 && (
              <div className="vst-related-group">
                <span>Drivers: {selection.relatedDriverIds.join(', ')}</span>
              </div>
            )}
          </div>
        )}

        {/* Explain result */}
        {explainError && (
          <div className="vst-explain-error">
            <AlertTriangle size={14} />
            <span>{explainError}</span>
          </div>
        )}
        {explainData && <ExplainResult data={explainData} />}
      </div>
    </div>
  );
}
